'use client';

/**
 * Connectivity and sync state for the whole application.
 *
 * The contract with the user (spec §85) is that a transaction is never silently
 * lost. Anything saved while offline is visibly "waiting to sync", the state is
 * named in words rather than implied by a colour, and the result of each sync is
 * reported — including the ones that could not be applied.
 */
import * as React from 'react';
import { toast } from 'sonner';
import {
  countPending,
  deviceId,
  foreignPendingSummary,
  markFailed,
  markSynced,
  markSyncing,
  pendingOperations,
  reclaimStalledOperations,
  pruneSynced,
  type QueuedOperation,
} from './db';
import { offlineScope, useOfflineSession } from './session';

export type ConnectionState = 'online' | 'offline' | 'pending' | 'syncing' | 'synced' | 'failed';

export const CONNECTION_LABEL: Record<ConnectionState, string> = {
  online: 'Online',
  offline: 'Offline',
  pending: 'Waiting to sync',
  syncing: 'Syncing…',
  synced: 'All synced',
  failed: 'Sync failed',
};

type OfflineContextValue = {
  online: boolean;
  state: ConnectionState;
  pendingCount: number;
  syncing: boolean;
  /** Push the queue to the server now. Safe to call repeatedly. */
  sync: () => Promise<void>;
  /** Re-read the pending count after enqueuing something. */
  refresh: () => Promise<void>;
  /**
   * Unsynced work on this device belonging to somebody else.
   *
   * Surfaced rather than hidden: on a shared counter tablet the person signing
   * in needs to know that a colleague's sale is still waiting, because it is
   * their Customer Point's money too.
   */
  foreignPending: { count: number; owners: string[] };
};

const OfflineContext = React.createContext<OfflineContextValue | null>(null);

/** Whether the browser currently believes it has a connection. */
function isOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}

function subscribeToConnectivity(onChange: () => void): () => void {
  window.addEventListener('online', onChange);
  window.addEventListener('offline', onChange);
  return () => {
    window.removeEventListener('online', onChange);
    window.removeEventListener('offline', onChange);
  };
}

/** How many operations go in one request. Matches the server's batch limit. */
const BATCH_SIZE = 25;

export function OfflineProvider({ children }: { children: React.ReactNode }) {
  const [pendingCount, setPendingCount] = React.useState(0);
  const [foreignPending, setForeignPending] = React.useState<{ count: number; owners: string[] }>({
    count: 0,
    owners: [],
  });
  const [syncing, setSyncing] = React.useState(false);
  const [lastResult, setLastResult] = React.useState<'synced' | 'failed' | null>(null);
  const syncingRef = React.useRef(false);
  // Re-reads the queue whenever the signed-in user changes, so a shared device
  // never shows one person's pending count to the next.
  const session = useOfflineSession();

  const refresh = React.useCallback(async () => {
    const scope = offlineScope();
    // Reclaim here as well as in `sync`, so a row abandoned mid-flight is
    // reported as "waiting to sync" rather than sitting under a "Syncing…"
    // label that nothing is going to resolve.
    await reclaimStalledOperations(scope);
    const [mine, foreign] = await Promise.all([countPending(scope), foreignPendingSummary(scope)]);
    setPendingCount(mine);
    setForeignPending(foreign);
  }, []);

  const sync = React.useCallback(async () => {
    if (syncingRef.current || typeof navigator === 'undefined' || !navigator.onLine) return;

    const scope = offlineScope();

    // Anything a previous attempt left marked `syncing` — a closed tab, a
    // killed browser — is put back in line first. Without this it would sit
    // there permanently, counted as pending and never sent.
    await reclaimStalledOperations(scope);

    // Only this session's own queue is ever sent. Another user's unsynced work
    // stays on the device until they sign in and sync it themselves — replaying
    // it under the current session would attribute their sales to the wrong
    // person.
    const queued = await pendingOperations(scope);
    if (queued.length === 0) {
      await refresh();
      return;
    }

    syncingRef.current = true;
    setSyncing(true);

    try {
      for (let offset = 0; offset < queued.length; offset += BATCH_SIZE) {
        const batch = queued.slice(offset, offset + BATCH_SIZE);
        await syncOneBatch(batch);
      }
      await pruneSynced();
      setLastResult('synced');
    } catch {
      setLastResult('failed');
      toast.error('Could not reach the server. Your transactions are safe on this device and will sync later.');
    } finally {
      syncingRef.current = false;
      setSyncing(false);
      await refresh();
    }
  }, [refresh]);

  // Connectivity belongs to the browser, not to React. Subscribing to it
  // directly means there is no mirrored copy to fall out of step, and the
  // server render is told "online" so the first paint is not a false alarm.
  const online = React.useSyncExternalStore(subscribeToConnectivity, isOnline, () => true);

  // Sync as soon as the connection comes back, and count what is waiting.
  React.useEffect(() => {
    // `refresh` awaits the IndexedDB count before it sets any state, so this
    // does not cause the cascading render the rule guards against.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh();

    const handleOnline = () => void sync();
    void session;
    window.addEventListener('online', handleOnline);

    // A periodic attempt covers the case where the browser reports "online" but
    // the connection is actually unusable — common on a weak mobile signal.
    const timer = window.setInterval(() => void sync(), 60_000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.clearInterval(timer);
    };
  }, [refresh, sync, session]);

  const state: ConnectionState = !online
    ? 'offline'
    : syncing
      ? 'syncing'
      : pendingCount > 0
        ? lastResult === 'failed'
          ? 'failed'
          : 'pending'
        : 'online';

  const value = React.useMemo(
    () => ({ online, state, pendingCount, syncing, sync, refresh, foreignPending }),
    [online, state, pendingCount, syncing, sync, refresh, foreignPending],
  );

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
}

export function useOffline(): OfflineContextValue {
  const context = React.useContext(OfflineContext);
  if (!context) {
    // Rendered outside the provider (e.g. the sign-in screen) — behave as if
    // online rather than crashing the tree.
    return {
      online: true,
      state: 'online',
      pendingCount: 0,
      syncing: false,
      sync: async () => undefined,
      refresh: async () => undefined,
      foreignPending: { count: 0, owners: [] },
    };
  }
  return context;
}

/**
 * Send one batch and record what happened to each operation.
 *
 * A network failure throws so the caller can stop and retry later; a per-item
 * business failure is recorded against that item alone and the rest continue.
 */
async function syncOneBatch(batch: QueuedOperation[]): Promise<void> {
  const ids = batch.map((op) => op.id).filter((id): id is number => id !== undefined);
  await markSyncing(ids);

  let response: Response;
  try {
    response = await fetch('/api/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        deviceId: deviceId(),
        operations: batch.map((op) => ({
          clientTxnId: op.clientTxnId,
          scope: op.scope,
          payload: op.payload,
          queuedAt: op.queuedAt,
          // When it happened, not when it is being sent — this is what decides
          // which business day the server files it under.
          occurredAt: op.occurredAt,
        })),
      }),
    });
  } catch (error) {
    // The connection dropped mid-request. Whether the server saw it is unknown,
    // which is exactly what the idempotency key is for: put the batch back and
    // send it again. Leaving it `syncing` would strand it.
    for (const op of batch) {
      await markFailed(op, 'The connection dropped while sending. It will be sent again.');
    }
    throw error;
  }

  if (!response.ok) {
    for (const op of batch) {
      await markFailed(op, response.status === 401 ? 'Your session has expired. Please sign in again.' : 'The server could not be reached.');
    }
    throw new Error(`Sync failed with status ${response.status}`);
  }

  const body = (await response.json()) as {
    ok: boolean;
    data?: {
      results: Array<
        | {
            clientTxnId: string;
            status: 'synced';
            number?: string | null;
            replayed: boolean;
            lateSync?: boolean;
            closingVersion?: number | null;
          }
        | { clientTxnId: string; status: 'conflict'; reason: string }
        | { clientTxnId: string; status: 'failed'; reason: string }
      >;
    };
    message?: string;
  };

  if (!body.ok || !body.data) {
    for (const op of batch) await markFailed(op, body.message ?? 'The server rejected this batch.');
    throw new Error(body.message ?? 'Sync rejected');
  }

  const byId = new Map(body.data.results.map((r) => [r.clientTxnId, r]));
  let syncedCount = 0;
  let conflictCount = 0;
  let lateCount = 0;

  for (const op of batch) {
    const result = byId.get(op.clientTxnId);
    if (!result) {
      await markFailed(op, 'The server did not report a result for this transaction.');
      continue;
    }
    if (result.status === 'synced') {
      await markSynced(op, result.number ?? undefined, result.lateSync ?? false);
      syncedCount += 1;
      if (result.lateSync) lateCount += 1;
    } else if (result.status === 'conflict') {
      await markFailed(op, result.reason, 'conflict');
      conflictCount += 1;
    } else {
      await markFailed(op, result.reason);
    }
  }

  if (syncedCount > 0) {
    toast.success(
      syncedCount === 1
        ? 'Transaction synchronised successfully.'
        : `${syncedCount} transactions synchronised successfully.`,
    );
  }
  if (lateCount > 0) {
    // Said plainly rather than hidden: the day was already closed, the
    // transaction was still accepted, and the closing has been adjusted. A
    // manager needs to know the figures they signed off have moved.
    toast.warning(
      lateCount === 1
        ? 'One transaction reached the server after its day had been closed. It has been recorded on its original date and the closing updated.'
        : `${lateCount} transactions reached the server after their day had been closed. They have been recorded on their original dates and the closings updated.`,
    );
  }
  if (conflictCount > 0) {
    toast.warning(
      conflictCount === 1
        ? '1 transaction needs a manager to review it. Nothing has been lost.'
        : `${conflictCount} transactions need a manager to review them. Nothing has been lost.`,
    );
  }
}
